import { Mail, Phone, MapPin, Github, Twitter, Linkedin, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Footer = () => {
  const navigate = useNavigate();

  const services = [
    { name: 'Baremetal Servers', href: '/baremetal-servers' },
    { name: 'VPS Servers', href: '/vps-servers' },
    { name: 'Residential Proxies', href: '/residential-proxies' },
    { name: 'Ashburn Interconnect', href: '/ashburn-interconnect' },
    { name: 'Consultation', href: '/consultation' }
  ];

  const support = [
    { name: 'Contact Support', href: 'mailto:support@nexx.net' }
  ];

  const company = [
    { name: 'About Us', href: '/' },
    { name: 'Why Choose Us?', href: '/why-choose-us' },
    { name: 'Privacy Policy', href: '/privacy-policy' }
  ];

  const handleNavigation = (href: string) => {
    navigate(href);
    window.scrollTo(0, 0);
  };

  return (
    <footer className="bg-gradient-to-br from-gray-50 to-gray-100 text-gray-800 relative overflow-hidden section-drift">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Company Info - Larger column */}
          <div className="lg:col-span-2 space-y-4">
            <button onClick={() => handleNavigation('/')} className="block mb-3">
              <img 
                src="/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png" 
                alt="NEXX"
                className="h-10 w-auto"
              />
            </button>
            <p className="text-gray-600 leading-relaxed text-sm mb-4 max-w-md">
              Purpose-built infrastructure for scale, speed, and control. From residential proxies to bare metal servers, we deliver custom connectivity solutions built for serious operators.
            </p>

            {/* Compact horizontal contact info */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <div className="flex items-center space-x-1 flex-shrink-0">
                <MapPin className="h-3 w-3 text-nexx-green" />
                <span className="whitespace-nowrap">Beltsville, MD</span>
              </div>
              <span className="text-gray-400">•</span>
              <div className="flex items-center space-x-1 flex-shrink-0">
                <Phone className="h-3 w-3 text-nexx-green" />
                <a href="tel:+12402198426" className="hover:text-nexx-green transition-colors duration-300 whitespace-nowrap">
                  (240) 219-8426
                </a>
              </div>
              <span className="text-gray-400">•</span>
              <div className="flex items-center space-x-1 flex-shrink-0">
                <Mail className="h-3 w-3 text-nexx-green" />
                <a href="mailto:support@nexx.net" className="hover:text-nexx-green transition-colors duration-300 whitespace-nowrap">
                  support@nexx.net
                </a>
              </div>
            </div>

            <div className="flex space-x-2 mt-4">
              <a 
                href="https://x.com/NEXXTELECOM" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-8 h-8 bg-white rounded-lg flex items-center justify-center hover:bg-nexx-green hover:text-white transition-all duration-300 shadow-sm border border-gray-200"
                aria-label="Follow us on Twitter/X"
              >
                <Twitter className="h-3 w-3" />
              </a>
              <a 
                href="https://www.linkedin.com/company/nexx-telecom" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-8 h-8 bg-white rounded-lg flex items-center justify-center hover:bg-nexx-green hover:text-white transition-all duration-300 shadow-sm border border-gray-200"
                aria-label="Follow us on LinkedIn"
              >
                <Linkedin className="h-3 w-3" />
              </a>
              <a 
                href="#" 
                className="w-8 h-8 bg-white rounded-lg flex items-center justify-center hover:bg-nexx-green hover:text-white transition-all duration-300 shadow-sm border border-gray-200"
                aria-label="Follow us on GitHub"
              >
                <Github className="h-3 w-3" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Services</h2>
            <ul className="space-y-2">
              {services.map((link) => (
                <li key={link.name}>
                  <button 
                    onClick={() => handleNavigation(link.href)}
                    className="text-sm text-gray-600 hover:text-nexx-green transition-colors duration-300 block py-1 hover:translate-x-1 transform transition-transform text-left"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support & Company Combined */}
          <div className="space-y-6">
            {/* Support */}
            <div>
              <h2 className="text-base font-semibold text-gray-900 mb-4">Support</h2>
              <ul className="space-y-2">
                {support.map((link) => (
                  <li key={link.name}>
                    {link.href.startsWith('mailto:') ? (
                      <a 
                        href={link.href} 
                        className="text-sm text-gray-600 hover:text-nexx-green transition-colors duration-300 block py-1 hover:translate-x-1 transform transition-transform"
                      >
                        {link.name}
                      </a>
                    ) : (
                      <button 
                        onClick={() => handleNavigation(link.href)}
                        className="text-sm text-gray-600 hover:text-nexx-green transition-colors duration-300 block py-1 hover:translate-x-1 transform transition-transform text-left"
                      >
                        {link.name}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h2 className="text-base font-semibold text-gray-900 mb-4">Company</h2>
              <ul className="space-y-2">
                {company.map((link) => (
                  <li key={link.name}>
                    <button 
                      onClick={() => handleNavigation(link.href)}
                      className="text-sm text-gray-600 hover:text-nexx-green transition-colors duration-300 block py-1 hover:translate-x-1 transform transition-transform text-left"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Section - More Compact */}
        <div className="border-t border-gray-200 mt-8 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0">
            <p className="text-gray-500 text-sm">
              © 2025 NEXX TELECOM LLC. All Rights Reserved.
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-500 items-center justify-center md:justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center hover:text-nexx-green transition-colors duration-300 bg-transparent border-none outline-none focus:outline-none">
                  Terms of Service
                  <ChevronDown className="ml-1 h-3 w-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg z-50">
                  <DropdownMenuItem>
                    <button onClick={() => handleNavigation('/general-tos')} className="w-full text-gray-700 hover:text-nexx-green text-left">
                      General TOS
                    </button>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <button onClick={() => handleNavigation('/proxy-tos')} className="w-full text-gray-700 hover:text-nexx-green text-left">
                      Residential Proxy TOS
                    </button>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <button onClick={() => handleNavigation('/server-tos')} className="w-full text-gray-700 hover:text-nexx-green text-left">
                      Baremetal & VPS TOS
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <button onClick={() => handleNavigation('/refund-policy')} className="hover:text-nexx-green transition-colors duration-300">
                Refund Policy
              </button>
              <button onClick={() => handleNavigation('/privacy-policy')} className="hover:text-nexx-green transition-colors duration-300">
                Privacy Policy
              </button>
              <a href="#" className="hover:text-nexx-green transition-colors duration-300">Cookie Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
